import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { useSearchParams, Link, useNavigate, useLocation as useRouterLocation } from 'react-router-dom';
import { Search, SlidersHorizontal, X, ChevronDown, Grid, MapPin, Loader2, CheckSquare, Square, Bell, BellPlus, Bookmark, Clock, Star, History, Check, Home, ChevronRight, Package, Filter, Grid3x3, List } from 'lucide-react';
import axios from '../lib/axios';
import { API, useAuth, useIsland, useLanguage } from '../App';
import { usePersistedCategory } from '../hooks/usePersistedState';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ListingCard from '../components/ListingCard';
import DynamicFilters from '../components/DynamicFilters';
import CategoryLanding from '../components/CategoryLanding';
import PersonalizedRecommendations from '../components/marketplace/PersonalizedRecommendations';
import { getIconComponent, getSubcategoryIcon } from '../utils/categoryIcons';
import { toast } from 'sonner';
import BottomSheet from '../components/ui/bottom-sheet';
import { Breadcrumbs } from '../components/ui/Breadcrumbs';
import { safeClick } from '../utils/safeClick';
import { formatCategorySlug } from '../utils/formatters';
import { SafariDropdown } from '../components/ui/safari-dropdown';
import MobileMarketplaceHeader from '../components/MobileMarketplaceHeader';
import { getSubcategoryTranslation, getCategoryTranslation } from '../i18n-subcategories';
import { getCached, setCached, CACHE_KEYS, CACHE_TTL } from '../utils/cache';
import { getListingTypeLabel, getListingTypeOptions } from '../utils/listingTypes';

// Top-level categories that get a dedicated landing page
const TOP_LEVEL_CATEGORIES = ['vehicles', 'real-estate', 'electronics', 'fashion', 'furniture', 'services', 'jobs', 'outdoor-sports', 'watersport', 'pets', 'baby-kids', 'art-collectibles', 'music'];

// Helper to detect if URL is a video file
const isVideoUrl = (url) => {
  if (!url) return false;
  const videoExtensions = ['.mp4', '.webm', '.mov', '.m4v'];
  const lowerUrl = url.toLowerCase();
  return videoExtensions.some(ext => lowerUrl.includes(ext));
};

// Category-aware price presets - 3 quick pills per category (Low / Mid / High)
const PRICE_PRESETS = {
  'real-estate': [
    { min: '', max: '100000', label: '<$100K' },
    { min: '', max: '300000', label: '<$300K' },
    { min: '500000', max: '', label: '$500K+' },
  ],
  'vehicles': [
    { min: '', max: '5000', label: '<$5K' },
    { min: '', max: '15000', label: '<$15K' },
    { min: '25000', max: '', label: '$25K+' },
  ],
  'electronics': [
    { min: '', max: '100', label: '<$100' },
    { min: '', max: '300', label: '<$300' },
    { min: '500', max: '', label: '$500+' },
  ],
  'fashion': [
    { min: '', max: '50', label: '<$50' },
    { min: '', max: '150', label: '<$150' },
    { min: '250', max: '', label: '$250+' },
  ],
  'furniture': [
    { min: '', max: '200', label: '<$200' },
    { min: '', max: '500', label: '<$500' },
    { min: '1000', max: '', label: '$1K+' },
  ],
  'watersport': [
    { min: '', max: '2000', label: '<$2K' },
    { min: '', max: '10000', label: '<$10K' },
    { min: '15000', max: '', label: '$15K+' },
  ],
  'car-rental': [
    { min: '', max: '50', label: '<$50/day' },
    { min: '', max: '100', label: '<$100/day' },
    { min: '200', max: '', label: '$200+/day' },
  ],
  // Categories where price filter is less relevant
  'jobs': null,
  'services': [
    { min: '', max: '100', label: '<$100' },
    { min: '', max: '300', label: '<$300' },
    { min: '500', max: '', label: '$500+' },
  ],
  // Default for all other categories
  default: [
    { min: '', max: '100', label: '<$100' },
    { min: '', max: '500', label: '<$500' },
    { min: '1000', max: '', label: '$1K+' },
  ],
};

// Helper to get price presets for a category
const getPricePresets = (categoryId) => {
  if (!categoryId) return PRICE_PRESETS.default;
  const presets = PRICE_PRESETS[categoryId];
  if (presets === null) return null; // Hide price filter for this category
  return presets || PRICE_PRESETS.default;
};

// Skeleton component matching CategoryLanding layout to prevent flicker
const CategoryLandingSkeleton = () => (
  <div data-testid="category-landing-skeleton">
    {/* Breadcrumbs Skeleton - Fixed height wrapper to prevent layout shift, hidden on mobile */}
    <div className="hidden md:flex h-10 items-center mb-4">
      <div className="hidden md:flex items-center gap-2 text-sm">
        <div className="h-6 w-16 bg-secondary/40 rounded-lg animate-pulse" />
        <ChevronRight className="w-4 h-4 text-muted-foreground/30" />
        <div className="h-6 w-20 bg-secondary/40 rounded-lg animate-pulse" />
        <ChevronRight className="w-4 h-4 text-muted-foreground/30" />
        <div className="h-6 w-24 bg-secondary/50 rounded-lg animate-pulse" />
      </div>
    </div>

    {/* Hero Banner Skeleton - matches CategoryHeroBanner */}
    <div className="relative w-full overflow-hidden rounded-xl mb-4 shadow-lg">
      <div className="aspect-[6/1] bg-secondary/30 animate-pulse" />
    </div>

    {/* Section Heading Skeleton - matches "Browse {category}" heading */}
    <div className="h-7 w-40 bg-secondary/40 rounded-lg animate-pulse mt-6 mb-4" />

    {/* Subcategory Grid Skeleton - matches SubcategoryCard structure exactly */}
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-4">
      {Array.from({ length: 10 }).map((_, i) => (
        <div 
          key={i} 
          className="bg-white dark:bg-zinc-900/40 border border-border rounded-3xl overflow-hidden flex flex-col"
        >
          {/* Icon Container Skeleton - matches aspect-[4/3] with centered icon box */}
          <div className="aspect-[4/3] bg-gradient-to-b from-secondary/40 to-transparent flex items-center justify-center">
            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-muted/50 animate-pulse" />
          </div>
          {/* Content Skeleton - matches p-4 sm:p-5 padding */}
          <div className="p-4 sm:p-5">
            <div className="h-4 w-24 bg-muted rounded-lg mb-2 animate-pulse" />
            <div className="h-3 w-16 bg-muted/70 rounded-lg animate-pulse" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

// Marketplace Breadcrumbs Component - Uses reusable Breadcrumbs with fixed-height wrapper
const MarketplaceBreadcrumbs = ({ category, subcategory, categories, language }) => {
  // Find category info from API data, fallback to formatted slug for instant display
  const categoryInfo = categories.find(c => c.id === category);
  const categoryName = categoryInfo?.name || formatCategorySlug(category);
  
  // Translate subcategory if needed
  const translatedSubcategory = subcategory ? getSubcategoryTranslation(subcategory, language) : '';
  
  // Build breadcrumb items array
  const items = [
    { label: 'Home', href: '/' },
    { label: 'Categories', href: '/categories' }
  ];
  
  if (category) {
    if (subcategory) {
      // Category is a link when subcategory exists
      items.push({ label: categoryName, href: `/marketplace?category=${category}` });
      items.push({ label: translatedSubcategory }); // Last item - no href
    } else {
      // Category is the last item
      items.push({ label: categoryName }); // Last item - no href
    }
  }
  
  return (
    <div className="hidden md:flex h-10 items-center mb-4" data-testid="marketplace-breadcrumbs-wrapper">
      <Breadcrumbs 
        items={items} 
        data-testid="marketplace-breadcrumbs"
      />
    </div>
  );
};

const MarketplacePage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const routerLocation = useRouterLocation();
  const { user, token, loading: authLoading } = useAuth();
  const { currentIsland, setCurrentIsland } = useIsland();
  const { t, language } = useLanguage();
  
  // Persisted category for header sync
  const { setCategory: setPersistedCategory, setSubcategory: setPersistedSubcategory } = usePersistedCategory();
  
  const [listings, setListings] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false); // For "Load More" button state
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1); // Track current page separately for Load More
  const [showFilters, setShowFilters] = useState(false);
  const [showSaveSearchModal, setShowSaveSearchModal] = useState(false);
  const [saveSearchName, setSaveSearchName] = useState('');
  const [savingSearch, setSavingSearch] = useState(false);
  const scrollRestoredRef = React.useRef(false);
  const pendingScrollRestoreRef = React.useRef(null); // Store pending scroll position
  const [listingGridAds, setListingGridAds] = useState([]);
  const [showIslandPicker, setShowIslandPicker] = useState(false);
  
  // View mode state (grid/list) - persisted in localStorage
  const [viewMode, setViewMode] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('marketplaceViewMode') || 'grid';
    }
    return 'grid';
  });
  
  // Persist view mode changes
  useEffect(() => {
    localStorage.setItem('marketplaceViewMode', viewMode);
  }, [viewMode]);
  
  // Dynamic filters state - Top filterable properties from category
  const [quickFilterProperties, setQuickFilterProperties] = useState([]);
  const [quickFilterSheets, setQuickFilterSheets] = useState({}); // Track which quick filter sheets are open
  
  // Fallback search state - shown when category search returns no results
  const [fallbackResults, setFallbackResults] = useState([]);
  const [suggestedCategories, setSuggestedCategories] = useState([]);
  const [fallbackSearchTerm, setFallbackSearchTerm] = useState('');
  const [fallbackSearchedCategory, setFallbackSearchedCategory] = useState('');
  
  // Early category detection - check URL param BEFORE categories load to prevent flicker
  // This runs synchronously on first render to determine if we should show CategoryLanding skeleton
  const earlyDetectedCategory = useMemo(() => {
    const categoryParam = searchParams.get('category');
    const hasSubcategory = searchParams.get('subcategory');
    const hasSearch = searchParams.get('search') || searchParams.get('q');
    const hasFilters = searchParams.get('location') || searchParams.get('listing_type') || 
                       searchParams.get('type') || searchParams.get('min') || searchParams.get('max') ||
                       searchParams.get('minPrice') || searchParams.get('maxPrice') ||
                       searchParams.get('lat') || searchParams.get('lon');
    
    // If category param exists and no subcategory/search/filters, this is a pure category landing
    if (categoryParam && !hasSubcategory && !hasSearch && !hasFilters) {
      return categoryParam;
    }
    return null;
  }, [searchParams]);
  
  // Track if categories have loaded
  const categoriesLoaded = categories.length > 0;
  
  // Search history and focus state
  const [searchFocused, setSearchFocused] = useState(false);
  const [recentSearches, setRecentSearches] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('recentSearches') || '[]');
    } catch {
      return [];
    }
  });
  const [savedSearches, setSavedSearches] = useState([]);
  const searchInputRef = useRef(null);

  // Filter states - Initialize from URL params including dynamic props_* filters
  const [filters, setFilters] = useState(() => {
    const baseFilters = {
      search: searchParams.get('search') || searchParams.get('q') || '',
      category: searchParams.get('category') || '',
      subcategory: searchParams.get('subcategory') || '',
      location: searchParams.get('location') || '',
      listing_type: searchParams.get('listing_type') || searchParams.get('type') || '',
      min_price: searchParams.get('min') || searchParams.get('minPrice') || '',
      max_price: searchParams.get('max') || searchParams.get('maxPrice') || '',
      sort: searchParams.get('sort') || 'newest',
      page: parseInt(searchParams.get('page')) || 1,
      radius: searchParams.get('radius') || '',
      lat: searchParams.get('lat') || '',
      lon: searchParams.get('lon') || '',
    };
    // Also read props_* params from URL
    for (const [key, value] of searchParams.entries()) {
      if (key.startsWith('props_')) {
        baseFilters[key] = value;
      }
    }
    return baseFilters;
  });
  
  // Handle "Use my location" button click - Privacy by Design
  // Scroll restoration for back navigation
  useLayoutEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    
    // Check if we have a saved scroll position on mount
    const savedScrollY = sessionStorage.getItem('marketplaceScrollY');
    const savedFilters = sessionStorage.getItem('marketplaceFilters');
    
    if (savedScrollY && parseInt(savedScrollY) > 0) {
      // Store the pending scroll position - will be restored after listings load
      pendingScrollRestoreRef.current = parseInt(savedScrollY);
    }
  }, []);

  // Handle popstate (back/forward navigation)
  useEffect(() => {
    const handlePopState = () => {
      const savedScrollY = sessionStorage.getItem('marketplaceScrollY');
      if (savedScrollY && parseInt(savedScrollY) > 0) {
        pendingScrollRestoreRef.current = parseInt(savedScrollY);
        scrollRestoredRef.current = false; // Allow restoration again
      }
    };
    
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Restore scroll position after listings are loaded
  useEffect(() => {
    if (!loading && listings.length > 0 && pendingScrollRestoreRef.current && !scrollRestoredRef.current) {
      // Verify filters match before restoring scroll
      const savedFiltersStr = sessionStorage.getItem('marketplaceFilters');
      let shouldRestore = true;
      
      if (savedFiltersStr) {
        try {
          const savedFilters = JSON.parse(savedFiltersStr);
          // Only restore if we're on the same category/search view
          if (savedFilters.category !== filters.category || 
              savedFilters.subcategory !== filters.subcategory) {
            shouldRestore = false;
          }
        } catch (e) {
          // Invalid JSON, proceed with restore
        }
      }
      
      if (shouldRestore) {
        const targetScroll = pendingScrollRestoreRef.current;
        scrollRestoredRef.current = true;
        
        // Use requestAnimationFrame to ensure DOM is painted
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            window.scrollTo(0, targetScroll);
            // Clean up after successful restore
            sessionStorage.removeItem('marketplaceScrollY');
            sessionStorage.removeItem('marketplaceFilters');
            pendingScrollRestoreRef.current = null;
          });
        });
      } else {
        // Filters don't match, clear saved scroll
        sessionStorage.removeItem('marketplaceScrollY');
        sessionStorage.removeItem('marketplaceFilters');
        pendingScrollRestoreRef.current = null;
        scrollRestoredRef.current = true;
      }
    }
  }, [loading, listings.length, filters.category, filters.subcategory]);
  
  useEffect(() => {
    fetchCategories();
    fetchListingGridAds();
  }, [language]);

  // Fetch top filterable properties when category changes (for quick filter pills)
  useEffect(() => {
    const fetchQuickFilterProperties = async () => {
      if (!filters.category) {
        setQuickFilterProperties([]);
        return;
      }
      
      try {
        let url = `${API}/categories/${filters.category}/properties?lang=en`;
        if (filters.subcategory && !filters.subcategory.startsWith('all-')) {
          url += `&subcategory=${encodeURIComponent(filters.subcategory)}`;
        }
        const response = await axios.get(url);
        // Get only filterable properties, sorted by sort_order, take top 2
        const filterable = (response.data.properties || [])
          .filter(p => p.filterable)
          .sort((a, b) => (a.sort_order || 999) - (b.sort_order || 999))
          .slice(0, 2);
        setQuickFilterProperties(filterable);
      } catch (error) {
        console.error('Failed to fetch quick filter properties:', error);
        setQuickFilterProperties([]);
      }
    };
    
    fetchQuickFilterProperties();
  }, [filters.category, filters.subcategory]);

  // Fetch saved searches for logged-in users
  useEffect(() => {
    // Triple check: authLoading done + user exists + auth cookie present
    const hasAuthCookie = document.cookie.includes('access_token') || 
                          document.cookie.includes('session') ||
                          document.cookie.includes('auth');
    
    // Only fetch when ALL conditions are met
    if (!authLoading && user && hasAuthCookie) {
      const fetchSavedSearches = async () => {
        try {
          const response = await axios.get(`${API}/saved-searches`);
          setSavedSearches(response.data || []);
        } catch (error) {
          // Silent fail - session might have expired
        }
      };
      fetchSavedSearches();
    } else if (!authLoading && !user) {
      // Clear saved searches when user is definitely not logged in
      setSavedSearches([]);
    }
  }, [user, authLoading]);

  // Add search term to recent searches
  const addToRecentSearches = (term) => {
    if (!term.trim()) return;
    setRecentSearches(prevSearches => {
      const updated = [term, ...prevSearches.filter(s => s !== term)].slice(0, 5);
      localStorage.setItem('recentSearches', JSON.stringify(updated));
      return updated;
    });
  };

  // Handle search submission (for recent searches tracking)
  const handleSearchSubmit = (term) => {
    if (term.trim()) {
      addToRecentSearches(term.trim());
    }
    setSearchFocused(false);
  };

  // Clear recent searches
  const clearRecentSearches = () => {
    setRecentSearches([]);
    localStorage.removeItem('recentSearches');
    // Dispatch custom event so other components (like SearchBar) can sync
    window.dispatchEvent(new CustomEvent('recentSearchesCleared'));
  };

  // Save current search as a saved search
  const saveCurrentSearch = async () => {
    if (!user) {
      toast.error(language === 'nl' ? 'Log in om zoekopdrachten op te slaan' : 'Please log in to save searches');
      return;
    }
    
    // Build search criteria from current filters
    const searchFilters = {
      category: filters.category || '',
      subcategory: filters.subcategory || '',
      location: filters.location || '',
      listing_type: filters.listing_type || '',
      min_price: filters.min_price || '',
      max_price: filters.max_price || '',
    };
    
    // Generate a name for the search
    const nameParts = [];
    if (filters.search) nameParts.push(`"${filters.search}"`);
    if (filters.category) {
      const cat = categories.find(c => c.id === filters.category);
      if (cat) nameParts.push(cat.name);
    }
    if (filters.location) nameParts.push(filters.location);
    
    const searchName = nameParts.length > 0 ? nameParts.join(' in ') : 'All Listings';
    
    setSavingSearch(true);
    try {
      await axios.post(`${API}/saved-searches`, {
        name: searchName,
        query: filters.search || '',
        filters: searchFilters
      });
      toast.success(
        language === 'nl' 
          ? 'Zoekopdracht opgeslagen! Bekijk onder Profiel → Favorieten.' 
          : 'Search saved! View under Profile → Favorites.'
      );
      // Refresh saved searches
      const response = await axios.get(`${API}/saved-searches`);
      setSavedSearches(response.data || []);
    } catch (error) {
      console.error('Failed to save search:', error);
      toast.error(language === 'nl' ? 'Opslaan mislukt' : 'Failed to save search');
    } finally {
      setSavingSearch(false);
    }
  };
  
  // Continuously track scroll position for restoration
  useEffect(() => {
    let scrollTimeout;
    const handleScroll = () => {
      // Debounce scroll saving to avoid too many writes
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        sessionStorage.setItem('marketplaceScrollY', window.scrollY.toString());
        sessionStorage.setItem('marketplaceFilters', JSON.stringify({
          category: filters.category,
          subcategory: filters.subcategory,
          search: filters.search,
          page: filters.page
        }));
      }, 100);
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, [filters.category, filters.subcategory, filters.search, filters.page]);
  
  // Reset scroll restoration flag when navigating to a listing
  useEffect(() => {
    const handleClick = (e) => {
      // Check for both anchor links AND listing card clicks (which use navigate())
      const link = e.target.closest('a[href^="/listing/"]');
      const listingCard = e.target.closest('[data-testid^="listing-card-"], [data-testid^="listing-image-container-"]');
      
      if (link || listingCard) {
        // Reset the restored flag so scroll can be restored on return
        scrollRestoredRef.current = false;
      }
    };
    
    document.addEventListener('click', handleClick);
    
    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, []);

  // Sync filters when URL search params change (e.g., from MegaMenu navigation)
  // Note: We intentionally ignore 'page' changes here - Load More handles page updates separately
  useEffect(() => {
    const newCategory = searchParams.get('category') || '';
    const newSubcategory = searchParams.get('subcategory') || '';
    const newSearch = searchParams.get('search') || '';
    const newPage = parseInt(searchParams.get('page') || '1', 10);
    
    // Only update if the core filters differ (not page)
    // This prevents Load More from triggering a full refetch
    const coreFiltersChanged = 
      newCategory !== filters.category || 
      newSubcategory !== filters.subcategory || 
      newSearch !== filters.search;
    
    if (coreFiltersChanged) {
      setFilters(prev => ({
        ...prev,
        category: newCategory,
        subcategory: newSubcategory,
        search: newSearch,
        page: 1 // Reset to first page when category/search changes
      }));
      setCurrentPage(1);
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchListings();
  }, [filters, currentIsland]);

  // Refresh listings when user returns to this page (e.g., after placing/withdrawing a bid)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchListings();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [filters, currentIsland]);

  const fetchCategories = async () => {
    try {
      // Check cache first (5 min TTL)
      const cacheKey = `${CACHE_KEYS.CATEGORIES}_${language}`;
      const cached = getCached(cacheKey, CACHE_TTL.CATEGORIES);
      if (cached) {
        setCategories(cached);
        return;
      }
      
      const response = await axios.get(`${API}/categories?lang=${language}`);
      setCategories(response.data);
      
      // Cache the result
      setCached(cacheKey, response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchListingGridAds = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.category) params.append('category', filters.category);
      if (currentIsland) params.append('island', currentIsland);
      const response = await axios.get(`${API}/banners/listing-grid?${params}`);
      setListingGridAds(response.data);
    } catch (error) {
      console.error('Failed to fetch listing grid ads:', error);
    }
  };

  // Refetch ads when category or island changes
  useEffect(() => {
    fetchListingGridAds();
  }, [filters.category, currentIsland]);

  const fetchListings = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.category) params.append('category', filters.category);
      // Don't send "all-*" subcategories to API - they mean "show all in category"
      if (filters.subcategory && !filters.subcategory.startsWith('all-')) {
        params.append('subcategory', filters.subcategory);
      }
      if (filters.location) params.append('location', filters.location);
      if (filters.listing_type) params.append('listing_type', filters.listing_type);
      if (filters.min_price) params.append('min_price', filters.min_price);
      if (filters.max_price) params.append('max_price', filters.max_price);
      if (filters.lat && filters.lon) {
        params.append('lat', filters.lat);
        params.append('lon', filters.lon);
        params.append('radius', filters.radius || '50');
      }
      // Add island filter
      if (currentIsland) {
        params.append('island', currentIsland);
      }
      // Add dynamic property filters (props_*)
      Object.entries(filters).forEach(([key, value]) => {
        if (key.startsWith('props_') && value) {
          params.append(key, value);
        }
      });
      params.append('sort', filters.sort);
      params.append('page', filters.page);
      params.append('limit', 12);

      const response = await axios.get(`${API}/listings?${params}`);
      
      // If page > 1 and we're doing initial load (not load more), 
      // we need to load all pages up to current page for URL restoration
      if (filters.page > 1 && !loadingMore) {
        // Load all items from page 1 to current page for proper restoration
        const allListings = [];
        for (let p = 1; p <= filters.page; p++) {
          const pageParams = new URLSearchParams(params);
          pageParams.set('page', p);
          const pageResponse = await axios.get(`${API}/listings?${pageParams}`);
          allListings.push(...pageResponse.data.listings);
        }
        setListings(allListings);
      } else {
        setListings(response.data.listings);
      }
      
      setTotal(response.data.total);
      setPages(response.data.pages);
      setCurrentPage(filters.page); // Sync currentPage with initial load
      
      // Handle fallback search results
      if (response.data.fallback_results && response.data.fallback_results.length > 0) {
        setFallbackResults(response.data.fallback_results);
        setSuggestedCategories(response.data.suggested_categories || []);
        setFallbackSearchTerm(response.data.search_term || '');
        setFallbackSearchedCategory(response.data.searched_category || '');
      } else {
        // Clear fallback state when we have results
        setFallbackResults([]);
        setSuggestedCategories([]);
        setFallbackSearchTerm('');
        setFallbackSearchedCategory('');
      }
    } catch (error) {
      console.error('Failed to fetch listings:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Load More function - appends next page to existing listings
  const handleLoadMore = async () => {
    if (loadingMore || currentPage >= pages) return;
    
    setLoadingMore(true);
    const nextPage = currentPage + 1;
    
    try {
      const params = new URLSearchParams();
      if (filters.category) params.append('category', filters.category);
      if (filters.subcategory) params.append('subcategory', filters.subcategory);
      if (filters.search) params.append('search', filters.search);
      if (filters.listing_type) params.append('listing_type', filters.listing_type);
      if (filters.condition) params.append('condition', filters.condition);
      if (filters.min_price) params.append('min_price', filters.min_price);
      if (filters.max_price) params.append('max_price', filters.max_price);
      if (filters.lat && filters.lon) {
        params.append('lat', filters.lat);
        params.append('lon', filters.lon);
        params.append('radius', filters.radius || '50');
      }
      if (currentIsland) {
        params.append('island', currentIsland);
      }
      Object.entries(filters).forEach(([key, value]) => {
        if (key.startsWith('props_') && value) {
          params.append(key, value);
        }
      });
      params.append('sort', filters.sort);
      params.append('page', nextPage);
      params.append('limit', 12);

      const response = await axios.get(`${API}/listings?${params}`);
      
      // Append new listings to existing ones
      setListings(prev => [...prev, ...response.data.listings]);
      
      // Update current page (does NOT trigger refetch)
      setCurrentPage(nextPage);
      
      // Update URL params for back button support (without triggering filters useEffect)
      const urlParams = new URLSearchParams(searchParams);
      urlParams.set('page', nextPage);
      setSearchParams(urlParams, { replace: true });
      
    } catch (error) {
      console.error('Failed to load more listings:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  const updateFilter = (key, value) => {
    let newFilters = { ...filters, [key]: value, page: key === 'page' ? value : 1 };
    
    // Clear dynamic property filters when category changes
    if (key === 'category') {
      Object.keys(newFilters).forEach(k => {
        if (k.startsWith('props_')) {
          delete newFilters[k];
        }
      });
      // Also clear subcategory when category changes
      newFilters.subcategory = '';
      // Sync with header navbar
      setPersistedCategory(value);
      setPersistedSubcategory('');
    }
    
    // Sync subcategory with header navbar
    if (key === 'subcategory') {
      setPersistedSubcategory(value);
    }
    
    // Only scroll to top when filters change (not page - Load More handles its own scroll)
    if (key !== 'page') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    
    setFilters(newFilters);
    
    // Update URL params
    const params = new URLSearchParams();
    Object.entries(newFilters).forEach(([k, v]) => {
      if (v && v !== 'newest' && v !== 1) params.set(k, v);
    });
    setSearchParams(params, { replace: key === 'page' }); // Replace history for pagination to allow proper back
  };

  // Update both min and max price in one call to avoid race conditions
  const updatePriceFilter = (minPrice, maxPrice) => {
    let newFilters = { ...filters, min_price: minPrice, max_price: maxPrice, page: 1 };
    setFilters(newFilters);
    
    // Update URL params
    const params = new URLSearchParams();
    Object.entries(newFilters).forEach(([k, v]) => {
      if (v && v !== 'newest' && v !== 1) params.set(k, v);
    });
    setSearchParams(params);
  };

  const clearFilters = () => {
    const cleared = {
      search: '',
      category: '',
      subcategory: '',
      location: '',
      listing_type: '',
      min_price: '',
      max_price: '',
      sort: 'newest',
      page: 1,
      radius: '',
      lat: '',
      lon: '',
    };
    setFilters(cleared);
    setSearchParams({});
    // Sync with header navbar
    setPersistedCategory('');
    setPersistedSubcategory('');
    // Navigate to categories page for a clean starting point
    navigate('/categories');
  };

  // Clear category filter but keep search term - used by fallback "Search everywhere" button
  const clearCategoryKeepSearch = useCallback(() => {
    const newFilters = {
      ...filters,
      category: '',
      subcategory: '',
      page: 1,
    };
    // Clear dynamic property filters
    Object.keys(newFilters).forEach(k => {
      if (k.startsWith('props_')) {
        delete newFilters[k];
      }
    });
    setFilters(newFilters);
    // Sync with header navbar
    setPersistedCategory('');
    setPersistedSubcategory('');
    
    // Update URL params - keep search term
    const params = new URLSearchParams();
    Object.entries(newFilters).forEach(([k, v]) => {
      if (v && v !== 'newest' && v !== 1) params.set(k, v);
    });
    setSearchParams(params);
  }, [filters, setSearchParams]);

  // Handler specifically for removing the category chip - navigates to /categories
  const handleRemoveCategoryFilter = useCallback(() => {
    const cleared = {
      search: '',
      category: '',
      subcategory: '',
      location: '',
      listing_type: '',
      min_price: '',
      max_price: '',
      sort: 'newest',
      page: 1,
      radius: '',
      lat: '',
      lon: '',
    };
    setFilters(cleared);
    setSearchParams({});
    navigate('/categories');
  }, [navigate, setSearchParams]);

  // Clear price filters only (min and max)
  const clearPriceFilter = useCallback(() => {
    const newFilters = { ...filters, min_price: '', max_price: '' };
    setFilters(newFilters);
    
    // Update URL params
    const params = new URLSearchParams();
    Object.entries(newFilters).forEach(([k, v]) => {
      if (v && v !== 'newest' && v !== 1) params.set(k, v);
    });
    setSearchParams(params);
  }, [filters, setSearchParams]);

  // Clear filters but keep the current category and subcategory
  const clearFiltersKeepCategory = () => {
    const currentCategory = filters.category;
    const currentSubcategory = filters.subcategory;
    const cleared = {
      search: '',
      category: currentCategory,
      subcategory: currentSubcategory,
      location: '',
      listing_type: '',
      min_price: '',
      max_price: '',
      sort: 'newest',
      page: 1,
      radius: '',
      lat: '',
      lon: '',
    };
    // Also clear dynamic property filters (props_*)
    setFilters(cleared);
    
    // Update URL params - keep category and subcategory
    const params = new URLSearchParams();
    if (currentCategory) params.set('category', currentCategory);
    if (currentSubcategory) params.set('subcategory', currentSubcategory);
    setSearchParams(params);
  };

  // Reset only dynamic property filters (props_*)
  const resetDynamicFilters = () => {
    const newFilters = { ...filters };
    Object.keys(newFilters).forEach(key => {
      if (key.startsWith('props_')) {
        delete newFilters[key];
      }
    });
    newFilters.page = 1;
    setFilters(newFilters);
    
    // Update URL
    const params = new URLSearchParams();
    Object.entries(newFilters).forEach(([k, v]) => {
      if (v && v !== 'newest' && v !== 1) params.set(k, v);
    });
    setSearchParams(params);
  };

  // Clear only location and dynamic filters (used in More Filters sheet)
  const clearMoreFilters = useCallback(() => {
    const newFilters = { ...filters };
    // Clear location-related filters
    newFilters.location = '';
    newFilters.lat = '';
    newFilters.lon = '';
    newFilters.radius = '';
    // Clear dynamic property filters (props_*)
    Object.keys(newFilters).forEach(key => {
      if (key.startsWith('props_')) {
        delete newFilters[key];
      }
    });
    newFilters.page = 1;
    
    setFilters(newFilters);
    
    // Update URL
    const params = new URLSearchParams();
    Object.entries(newFilters).forEach(([k, v]) => {
      if (v && v !== 'newest' && v !== 1) params.set(k, v);
    });
    setSearchParams(params);
  }, [filters, setSearchParams]);

  const activeFiltersCount = Object.entries(filters).filter(
    ([key, value]) => value && key !== 'sort' && key !== 'page' && key !== 'lat' && key !== 'lon'
  ).length;

  // Check if there's an active search to save
  const hasActiveSearch = filters.search || filters.category || filters.location || filters.listing_type || filters.min_price || filters.max_price;

  const handleSaveSearch = async () => {
    if (!user) {
      toast.error(language === 'nl' ? 'Log in om zoekopdrachten op te slaan' : 'Please sign in to save searches');
      navigate('/auth');
      return;
    }
    
    if (!hasActiveSearch) {
      toast.error(language === 'nl' ? 'Voeg eerst filters of zoektermen toe' : 'Add some filters or search terms first');
      return;
    }
    
    setShowSaveSearchModal(true);
    // Generate default name based on active filters
    const parts = [];
    if (filters.search) parts.push(`"${filters.search}"`);
    if (filters.subcategory && !filters.subcategory.startsWith('all-')) {
      parts.push(getSubcategoryTranslation(filters.subcategory, language));
    } else if (filters.category) {
      const categoryInfo = categories.find(c => c.id === filters.category);
      parts.push(categoryInfo?.name || formatCategorySlug(filters.category));
    }
    if (filters.location) parts.push(filters.location);
    if (filters.listing_type) parts.push(filters.listing_type);
    if (filters.min_price || filters.max_price) {
      const priceRange = filters.min_price && filters.max_price 
        ? `$${filters.min_price}-$${filters.max_price}`
        : filters.min_price ? `$${filters.min_price}+` : `Up to $${filters.max_price}`;
      parts.push(priceRange);
    }
    setSaveSearchName(parts.join(' • ') || 'My Search');
  };

  const confirmSaveSearch = async () => {
    if (!saveSearchName.trim()) {
      toast.error(language === 'nl' ? 'Voer een naam in voor deze zoekopdracht' : 'Please enter a name for this search');
      return;
    }
    
    setSavingSearch(true);
    try {
      // Build complete filters object including all active filters
      const searchFilters = {
        category: filters.category || '',
        subcategory: filters.subcategory || '',
        location: filters.location || '',
        listing_type: filters.listing_type || '',
        min_price: filters.min_price || '',
        max_price: filters.max_price || '',
        sort: filters.sort || 'newest',
        radius: filters.radius || '',
        lat: filters.lat || '',
        lon: filters.lon || '',
      };
      
      // Include dynamic property filters (props_*)
      Object.keys(filters).forEach(key => {
        if (key.startsWith('props_') && filters[key]) {
          searchFilters[key] = filters[key];
        }
      });
      
      // Include island filter if set
      if (currentIsland) {
        searchFilters.island = currentIsland;
      }
      
      await axios.post(`${API}/saved-searches`, {
        name: saveSearchName.trim(),
        query: filters.search || '',
        filters: searchFilters
      });
      toast.success(
        language === 'nl' 
          ? 'Zoekopdracht opgeslagen! Bekijk je opgeslagen zoekopdrachten onder Profiel → Favorieten.' 
          : 'Search saved! View your saved searches under Profile → Favorites.'
      );
      setShowSaveSearchModal(false);
      setSaveSearchName('');
    } catch (error) {
      toast.error(language === 'nl' ? 'Opslaan mislukt' : 'Failed to save search');
    } finally {
      setSavingSearch(false);
    }
  };

  // Get active category info
  const activeCategoryInfo = categories.find(c => c.id === filters.category);

  // Check if subcategory is "all-[category]" pattern (View All mode)
  const isViewAllMode = filters.subcategory?.startsWith('all-');

  // Detect "pure category" page - category selected but no search, no subcategory, no other filters
  // Note: "all-*" subcategories are NOT pure category pages - they show the full marketplace grid
  const isPureCategoryPage = Boolean(
    filters.category && 
    activeCategoryInfo &&
    !filters.search &&
    !filters.subcategory && // all-* subcategories will make this false
    !filters.location &&
    !filters.listing_type &&
    !filters.min_price &&
    !filters.max_price &&
    !filters.lat &&
    !filters.lon &&
    // Check no dynamic props filters
    !Object.keys(filters).some(k => k.startsWith('props_') && filters[k])
  );

  // Handler for navigating from category landing to subcategory
  const handleNavigateToSubcategory = (subcategory) => {
    if (subcategory) {
      updateFilter('subcategory', subcategory);
    } else {
      // View all - use dedicated URL pattern: subcategory=all-[category]
      // This ensures proper back-navigation and clear "Discovery" mode
      updateFilter('subcategory', `all-${filters.category}`);
    }
  };

  // Handler for back button on category landing
  const handleBackFromCategory = () => {
    navigate(-1);
  };

  // State for mobile filter dropdowns
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showSubcategoryDropdown, setShowSubcategoryDropdown] = useState(false);
  const [showPriceDropdown, setShowPriceDropdown] = useState(false);
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const [showSortDropdown, setShowSortDropdown] = useState(false); // Desktop dropdown
  const [showSortSheet, setShowSortSheet] = useState(false); // Mobile bottom sheet
  
  // Refs for dropdown scroll containers
  const categoryScrollRef = useRef(null);
  const subcategoryScrollRef = useRef(null);
  const sortDropdownRef = useRef(null);

  // Click outside handler for sort dropdown
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(e.target)) {
        setShowSortDropdown(false);
      }
    };
    if (showSortDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('touchstart', handleClickOutside);
      };
    }
  }, [showSortDropdown]);

  // Body scroll lock for mobile filter dropdowns (NOT for sort dropdown - it's small)
  useEffect(() => {
    const isAnyQuickFilterOpen = Object.values(quickFilterSheets).some(v => v);
    const isAnyDropdownOpen = showCategoryDropdown || showSubcategoryDropdown || showPriceDropdown || showTypeDropdown || showFilters || isAnyQuickFilterOpen;
    if (isAnyDropdownOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [showCategoryDropdown, showSubcategoryDropdown, showPriceDropdown, showTypeDropdown, showFilters, quickFilterSheets]);
  
  // Scroll dropdowns to top when opened (useLayoutEffect for synchronous execution)
  useLayoutEffect(() => {
    if (showCategoryDropdown && categoryScrollRef.current) {
      categoryScrollRef.current.scrollTop = 0;
    }
  }, [showCategoryDropdown]);
  
  useLayoutEffect(() => {
    if (showSubcategoryDropdown && subcategoryScrollRef.current) {
      subcategoryScrollRef.current.scrollTop = 0;
    }
  }, [showSubcategoryDropdown]);

  return (
    <div className="min-h-screen bg-background" data-testid="marketplace-page">
      {/* Mobile Bottom Sheets - Rendered at root level for proper z-index */}
      
      {/* Sort Sheet - Mobile only */}
      <BottomSheet
        isOpen={showSortSheet}
        onClose={() => setShowSortSheet(false)}
        title="Sort By"
        testId="sort-sheet"
        maxHeight="50vh"
      >
        <div className="py-2">
          {[
            { value: 'newest', label: 'Newest First' },
            { value: 'price_low', label: 'Price: Low to High' },
            { value: 'price_high', label: 'Price: High to Low' },
            { value: 'popular', label: 'Most Popular' }
          ].map(option => (
            <button
              key={option.value}
              onClick={() => { updateFilter('sort', option.value); setShowSortSheet(false); }}
              className={`w-full px-4 py-3.5 text-left text-sm flex items-center justify-between ${
                filters.sort === option.value ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-secondary'
              }`}
            >
              <span>{option.label}</span>
              {filters.sort === option.value && <Check className="w-4 h-4" />}
            </button>
          ))}
        </div>
      </BottomSheet>
      
      {/* Category Sheet */}
      <BottomSheet
        isOpen={showCategoryDropdown}
        onClose={() => setShowCategoryDropdown(false)}
        title="Select Category"
        testId="category-sheet"
        fullHeight={true}
      >
        <div ref={categoryScrollRef} className="py-1">
          <button onClick={() => { updateFilter('category', ''); setShowCategoryDropdown(false); }}
            className={`w-full px-4 py-3.5 text-left text-sm flex items-center gap-3 ${!filters.category ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-secondary'}`}>
            <Grid className="w-5 h-5" /><span className="flex-1">All Categories</span>
            {!filters.category && <Check className="w-4 h-4" />}
          </button>
          {categories.map((cat) => {
            const CatIcon = getIconComponent(cat.icon);
            return (
            <button key={cat.id} onClick={() => { updateFilter('category', cat.id); setShowCategoryDropdown(false); }}
              className={`w-full px-4 py-3.5 text-left text-sm flex items-center gap-3 ${filters.category === cat.id ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-secondary'}`}>
              <CatIcon className="w-5 h-5" /><span className="flex-1">{cat.name}</span>
              {filters.category === cat.id && <Check className="w-4 h-4" />}
            </button>
          );})}
        </div>
      </BottomSheet>
      
      {/* Subcategory Sheet */}
      <BottomSheet
        isOpen={showSubcategoryDropdown && filters.category && activeCategoryInfo?.subcategories?.length > 0}
        onClose={() => setShowSubcategoryDropdown(false)}
        title="Select Subcategory"
        testId="subcategory-sheet"
        fullHeight={true}
      >
        <div ref={subcategoryScrollRef} className="py-1">
          {activeCategoryInfo && (
            <>
              <button onClick={() => { updateFilter('subcategory', ''); setShowSubcategoryDropdown(false); }}
                className={`w-full px-4 py-3.5 text-left text-sm flex items-center gap-3 ${!filters.subcategory ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-secondary'}`}>
                <Grid className="w-5 h-5" /><span className="flex-1">All {activeCategoryInfo.name}</span>
                {!filters.subcategory && <Check className="w-4 h-4" />}
              </button>
              {activeCategoryInfo.subcategories?.map(sub => {
                // Support both old string format and new object format
                const subId = typeof sub === 'object' ? sub.id : sub;
                const subName = typeof sub === 'object' ? sub.name : sub;
                const SubIcon = getSubcategoryIcon(subId);
                return (
                <button key={subId} onClick={() => { updateFilter('subcategory', subId); setShowSubcategoryDropdown(false); }}
                  className={`w-full px-4 py-3.5 text-left text-sm flex items-center gap-3 ${filters.subcategory === subId ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-secondary'}`}>
                  <SubIcon className="w-5 h-5" /><span className="flex-1">{getSubcategoryTranslation(subName, language)}</span>
                  {filters.subcategory === subId && <Check className="w-4 h-4" />}
                </button>
              );})}
            </>
          )}
        </div>
      </BottomSheet>
      
      {/* Price Sheet - Category-aware presets */}
      <BottomSheet
        isOpen={showPriceDropdown}
        onClose={() => setShowPriceDropdown(false)}
        title="Price Range"
        testId="price-sheet"
        maxHeight="50vh"
      >
        <div className="p-4 space-y-4">
          <div className="flex gap-3">
            <input type="number" placeholder="Min" value={filters.min_price} onChange={(e) => updateFilter('min_price', e.target.value)} className="w-full px-3 py-3 text-sm border border-border rounded-xl bg-background" />
            <input type="number" placeholder="Max" value={filters.max_price} onChange={(e) => updateFilter('max_price', e.target.value)} className="w-full px-3 py-3 text-sm border border-border rounded-xl bg-background" />
          </div>
          <div className="flex gap-2 flex-wrap">
            {/* Clear/Any button */}
            <button 
              onClick={() => updatePriceFilter('', '')} 
              className={`px-4 py-2.5 text-sm rounded-xl transition-colors ${!filters.min_price && !filters.max_price ? 'bg-primary text-primary-foreground' : 'bg-secondary hover:bg-secondary/80'}`}
            >
              Any
            </button>
            {/* Category-specific price presets */}
            {(getPricePresets(filters.category) || PRICE_PRESETS.default).map((preset, i) => (
              <button 
                key={i} 
                onClick={() => updatePriceFilter(preset.min, preset.max)} 
                className={`px-4 py-2.5 text-sm rounded-xl transition-colors ${filters.min_price === preset.min && filters.max_price === preset.max ? 'bg-primary text-primary-foreground' : 'bg-secondary hover:bg-secondary/80'}`}
              >
                {preset.label}
              </button>
            ))}
          </div>
          <button onClick={() => setShowPriceDropdown(false)} className="w-full py-3 bg-primary text-primary-foreground text-sm font-medium rounded-xl">Apply</button>
        </div>
      </BottomSheet>
      
      {/* Type Sheet */}
      <BottomSheet
        isOpen={showTypeDropdown}
        onClose={() => setShowTypeDropdown(false)}
        title="Listing Type"
        testId="type-sheet"
        maxHeight="50vh"
      >
        <div className="py-1">
          {[['', language === 'nl' ? 'Alle Types' : language === 'es' ? 'Todos los Tipos' : 'All Types'], 
            ['sale', getListingTypeLabel('sale', language)], 
            ['rent', getListingTypeLabel('rent', language)], 
            ['trade', getListingTypeLabel('trade', language)], 
            ['service', getListingTypeLabel('service', language)],
            ['yard_sale', getListingTypeLabel('yard_sale', language)],
            ['auction', getListingTypeLabel('auction', language)]
          ].map(([value, label]) => (
            <button key={value} onClick={() => { updateFilter('listing_type', value); setShowTypeDropdown(false); }}
              className={`w-full px-4 py-3.5 text-left text-sm flex items-center justify-between ${filters.listing_type === value ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-secondary'}`}>
              {label}
              {filters.listing_type === value && <Check className="w-4 h-4" />}
            </button>
          ))}
        </div>
      </BottomSheet>
      
      {/* Quick Dynamic Filter Sheets - One for each quick filter property */}
      {quickFilterProperties.map((prop) => {
        const propKey = `props_${prop.key}`;
        const propLabel = typeof prop.name === 'object' ? prop.name.en : prop.name;
        const currentValue = filters[propKey];
        const isOpen = quickFilterSheets[prop.key];
        
        // For predefined_list type
        if (prop.type === 'predefined_list' && prop.options?.length) {
          return (
            <BottomSheet
              key={prop.key}
              isOpen={isOpen}
              onClose={() => setQuickFilterSheets(prev => ({ ...prev, [prop.key]: false }))}
              title={propLabel}
              testId={`quick-filter-sheet-${prop.key}`}
              fullHeight={true}
            >
              <div className="py-1">
                {/* Clear option */}
                <button 
                  onClick={() => { updateFilter(propKey, ''); setQuickFilterSheets(prev => ({ ...prev, [prop.key]: false })); }}
                  className={`w-full px-4 py-3.5 text-left text-sm flex items-center justify-between ${!currentValue ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-secondary'}`}
                >
                  Any {propLabel}
                  {!currentValue && <Check className="w-4 h-4" />}
                </button>
                {prop.options.map((opt) => {
                  const optValue = typeof opt === 'object' ? (opt.value || String(opt)) : String(opt);
                  const optLabel = typeof opt === 'object' ? (opt.label || opt.value || String(opt)) : String(opt);
                  return (
                    <button 
                      key={optValue} 
                      onClick={() => { updateFilter(propKey, optValue); setQuickFilterSheets(prev => ({ ...prev, [prop.key]: false })); }}
                      className={`w-full px-4 py-3.5 text-left text-sm flex items-center justify-between ${currentValue === optValue ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-secondary'}`}
                    >
                      {optLabel}
                      {currentValue === optValue && <Check className="w-4 h-4" />}
                    </button>
                  );
                })}
              </div>
            </BottomSheet>
          );
        }
        
        // For number type (range inputs)
        if (prop.type === 'number') {
          const minKey = `props_${prop.key}_min`;
          const maxKey = `props_${prop.key}_max`;
          const minValue = filters[minKey] || '';
          const maxValue = filters[maxKey] || '';
          
          return (
            <BottomSheet
              key={prop.key}
              isOpen={isOpen}
              onClose={() => setQuickFilterSheets(prev => ({ ...prev, [prop.key]: false }))}
              title={`${propLabel}${prop.unit ? ` (${prop.unit})` : ''}`}
              testId={`quick-filter-sheet-${prop.key}`}
              maxHeight="50vh"
            >
              <div className="p-4 space-y-4">
                <div className="flex gap-3">
                  <input 
                    type="number" 
                    placeholder={prop.min_value ? `Min ${prop.min_value}` : 'Min'} 
                    value={minValue}
                    onChange={(e) => updateFilter(minKey, e.target.value)} 
                    className="w-full px-3 py-3 text-sm border border-border rounded-xl bg-background" 
                  />
                  <input 
                    type="number" 
                    placeholder={prop.max_value ? `Max ${prop.max_value}` : 'Max'} 
                    value={maxValue}
                    onChange={(e) => updateFilter(maxKey, e.target.value)} 
                    className="w-full px-3 py-3 text-sm border border-border rounded-xl bg-background" 
                  />
                </div>
                <div className="flex gap-2">
                  <button 
                    onClick={() => { updateFilter(minKey, ''); updateFilter(maxKey, ''); }}
                    className={`px-4 py-2.5 text-sm rounded-xl transition-colors ${!minValue && !maxValue ? 'bg-primary text-primary-foreground' : 'bg-secondary hover:bg-secondary/80'}`}
                  >
                    Any
                  </button>
                </div>
                <button 
                  onClick={() => setQuickFilterSheets(prev => ({ ...prev, [prop.key]: false }))} 
                  className="w-full py-3 bg-primary text-primary-foreground text-sm font-medium rounded-xl"
                >
                  Apply
                </button>
              </div>
            </BottomSheet>
          );
        }
        
        // For text type (simple input with search)
        return (
          <BottomSheet
            key={prop.key}
            isOpen={isOpen}
            onClose={() => setQuickFilterSheets(prev => ({ ...prev, [prop.key]: false }))}
            title={propLabel}
            testId={`quick-filter-sheet-${prop.key}`}
            maxHeight="40vh"
          >
            <div className="p-4 space-y-4">
              <input 
                type="text" 
                placeholder={`Search ${propLabel.toLowerCase()}...`}
                value={currentValue || ''}
                onChange={(e) => updateFilter(propKey, e.target.value)} 
                className="w-full px-3 py-3 text-sm border border-border rounded-xl bg-background" 
              />
              <div className="flex gap-2">
                <button 
                  onClick={() => { updateFilter(propKey, ''); setQuickFilterSheets(prev => ({ ...prev, [prop.key]: false })); }}
                  className="flex-1 py-3 bg-secondary text-foreground text-sm font-medium rounded-xl"
                >
                  Clear
                </button>
                <button 
                  onClick={() => setQuickFilterSheets(prev => ({ ...prev, [prop.key]: false }))} 
                  className="flex-1 py-3 bg-primary text-primary-foreground text-sm font-medium rounded-xl"
                >
                  Apply
                </button>
              </div>
            </div>
          </BottomSheet>
        );
      })}
      
      <Navbar />
      
      {/* Mobile-only Search & Filter Header - Compact breadcrumb + filter chips design */}
      {!((earlyDetectedCategory && !categoriesLoaded && !isViewAllMode) || (isPureCategoryPage)) && (
        <MobileMarketplaceHeader
          filters={filters}
          updateFilter={updateFilter}
          clearFiltersKeepCategory={clearFiltersKeepCategory}
          categories={categories}
          activeCategoryInfo={activeCategoryInfo}
          setShowCategoryDropdown={setShowCategoryDropdown}
          setShowSubcategoryDropdown={setShowSubcategoryDropdown}
          setShowFilters={setShowFilters}
          searchFocused={searchFocused}
          setSearchFocused={setSearchFocused}
          recentSearches={recentSearches}
          clearRecentSearches={clearRecentSearches}
          handleSearchSubmit={handleSearchSubmit}
          currentIsland={currentIsland}
          setCurrentIsland={setCurrentIsland}
          showIslandPicker={showIslandPicker}
          setShowIslandPicker={setShowIslandPicker}
          total={total}
          setShowSortSheet={setShowSortSheet}
          quickFilterProperties={quickFilterProperties}
          quickFilterSheets={quickFilterSheets}
          setQuickFilterSheets={setQuickFilterSheets}
          activeFiltersCount={activeFiltersCount}
          getPricePresets={getPricePresets}
          onSaveSearch={handleSaveSearch}
          hasActiveSearch={hasActiveSearch}
          user={user}
          viewMode={viewMode}
          setViewMode={setViewMode}
        />
      )}

      {/* Saved Search Indicator */}
      {searchParams.get('savedSearch') && (
        <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border-b border-amber-500/20">
          <div className="container mx-auto px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg shadow-amber-500/20">
                  <Bookmark className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-amber-700 dark:text-amber-400">
                    Saved Search: {searchParams.get('savedSearch')}
                  </p>
                  <p className="text-xs text-amber-600/70 dark:text-amber-500/70">
                    Showing results for your saved search
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  // Remove saved search params and keep other filters
                  const newParams = new URLSearchParams(searchParams);
                  newParams.delete('savedSearch');
                  newParams.delete('savedSearchId');
                  setSearchParams(newParams);
                }}
                className="flex items-center gap-2 px-3 py-1.5 text-amber-700 dark:text-amber-400 hover:bg-amber-500/10 rounded-lg transition-colors text-sm"
              >
                <X className="w-4 h-4" />
                Clear
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Save Search Modal */}
      {showSaveSearchModal && (
        <div className="fixed inset-0 bg-black/50 z-[150] flex items-center justify-center p-4" onClick={() => setShowSaveSearchModal(false)}>
          <div className="bg-card rounded-2xl p-6 max-w-md w-full shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-amber-100 dark:bg-amber-900/30 rounded-xl flex items-center justify-center">
                <Bell className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-foreground">Save This Search</h3>
                <p className="text-sm text-muted-foreground">Get notified when new listings match</p>
              </div>
            </div>
            
            <input
              type="text"
              value={saveSearchName}
              onChange={(e) => setSaveSearchName(e.target.value)}
              placeholder="Name this search..."
              className="w-full px-4 py-3 border border-border rounded-xl bg-secondary text-foreground focus:ring-2 focus:ring-primary/50 outline-none mb-4"
              data-testid="save-search-name-input"
            />
            
            <div className="p-3 bg-secondary/50 rounded-xl mb-4 text-sm">
              <p className="text-muted-foreground">Your search criteria:</p>
              <div className="flex flex-wrap gap-2 mt-2">
                {filters.search && (
                  <span className="px-2 py-1 bg-primary/10 text-primary rounded-lg text-xs font-medium">
                    "{filters.search}"
                  </span>
                )}
                {filters.category && (
                  <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg text-xs">
                    {categories.find(c => c.id === filters.category)?.name || formatCategorySlug(filters.category)}
                  </span>
                )}
                {filters.subcategory && !filters.subcategory.startsWith('all-') && (
                  <span className="px-2 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-lg text-xs">
                    {filters.subcategory}
                  </span>
                )}
                {filters.location && (
                  <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-lg text-xs flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {filters.location}
                  </span>
                )}
                {filters.listing_type && (
                  <span className="px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded-lg text-xs">
                    {getListingTypeLabel(filters.listing_type, language)}
                  </span>
                )}
                {(filters.min_price || filters.max_price) && (
                  <span className="px-2 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 rounded-lg text-xs">
                    {filters.min_price && filters.max_price 
                      ? `$${filters.min_price} - $${filters.max_price}`
                      : filters.min_price 
                        ? `$${filters.min_price}+` 
                        : `Up to $${filters.max_price}`}
                  </span>
                )}
                {currentIsland && (
                  <span className="px-2 py-1 bg-cyan-100 dark:bg-cyan-900/30 text-cyan-600 dark:text-cyan-400 rounded-lg text-xs flex items-center gap-1">
                    {currentIsland === 'curacao' ? '🇨🇼 Curaçao' : 
                     currentIsland === 'aruba' ? '🇦🇼 Aruba' : 
                     currentIsland === 'bonaire' ? '🇧🇶 Bonaire' : currentIsland}
                  </span>
                )}
                {filters.sort && filters.sort !== 'newest' && (
                  <span className="px-2 py-1 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded-lg text-xs">
                    {filters.sort === 'price_low' ? 'Price: Low to High' : 
                     filters.sort === 'price_high' ? 'Price: High to Low' : 
                     filters.sort === 'popular' ? 'Most Popular' : filters.sort}
                  </span>
                )}
                {/* Dynamic property filters */}
                {Object.entries(filters)
                  .filter(([key, value]) => key.startsWith('props_') && value)
                  .map(([key, value]) => (
                    <span key={key} className="px-2 py-1 bg-rose-100 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 rounded-lg text-xs">
                      {key.replace('props_', '').replace(/_/g, ' ')}: {value}
                    </span>
                  ))
                }
              </div>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowSaveSearchModal(false)}
                className="flex-1 px-4 py-2.5 border border-border rounded-xl hover:bg-secondary transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmSaveSearch}
                disabled={savingSearch}
                className="flex-1 px-4 py-2.5 bg-primary text-white rounded-xl hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="confirm-save-search-btn"
              >
                {savingSearch ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />}
                Save & Alert Me
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content with Sidebar Layout */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Category Landing Skeleton - Show immediately when category URL detected but data not loaded */}
        {/* Skip skeleton if we're in "View All" mode (subcategory=all-*) */}
        {earlyDetectedCategory && !categoriesLoaded && !isViewAllMode ? (
          <CategoryLandingSkeleton />
        ) : isPureCategoryPage ? (
          <CategoryLanding
            category={activeCategoryInfo}
            onNavigateToSubcategory={handleNavigateToSubcategory}
            onBack={handleBackFromCategory}
          />
        ) : (
        <>
          {/* Breadcrumbs - Show when category or subcategory is selected (above sidebar) */}
          {/* For "all-*" subcategories, show just the category in breadcrumbs */}
          {(filters.category || filters.subcategory) && (
            <MarketplaceBreadcrumbs 
              category={filters.category} 
              subcategory={isViewAllMode ? '' : filters.subcategory}
              categories={categories}
              language={language}
            />
          )}
          
          <div className="flex gap-6 min-h-[calc(100vh-200px)]">
          {/* Left Sidebar - Filters (Desktop) */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <div className="sticky top-24 max-h-[calc(100vh-7rem)] overflow-y-auto pr-1 pb-20 scrollbar-thin">
              {/* Saved Searches Section - For logged-in users */}
              {user && savedSearches.length > 0 && (
                <div className="bg-gradient-to-br from-amber-500/5 to-orange-500/5 rounded-xl border border-amber-500/20 p-4 mb-4">
                  <h3 className="text-sm font-medium text-foreground mb-3 flex items-center gap-2">
                    <Star className="w-4 h-4 text-amber-500" />
                    Saved Searches
                  </h3>
                  <div className="space-y-1">
                    {savedSearches.slice(0, 5).map((saved) => (
                      <button
                        key={saved.id}
                        onClick={() => {
                          const params = new URLSearchParams();
                          params.set('savedSearch', saved.name);
                          params.set('savedSearchId', saved.id);
                          if (saved.criteria?.search) params.set('search', saved.criteria.search);
                          if (saved.criteria?.category) params.set('category', saved.criteria.category);
                          if (saved.criteria?.location) params.set('location', saved.criteria.location);
                          if (saved.criteria?.min_price) params.set('min', saved.criteria.min_price);
                          if (saved.criteria?.max_price) params.set('max', saved.criteria.max_price);
                          if (saved.criteria?.listing_type) params.set('type', saved.criteria.listing_type);
                          setSearchParams(params);
                        }}
                        className="w-full text-left px-3 py-2 rounded-lg text-sm transition-colors hover:bg-amber-500/10 text-foreground flex items-center gap-2 group"
                      >
                        <Clock className="w-3.5 h-3.5 text-muted-foreground group-hover:text-amber-500 transition-colors" />
                        <span className="truncate">{saved.name}</span>
                      </button>
                    ))}
                  </div>
                  <Link
                    to="/profile?tab=saved-searches"
                    className="mt-2 block text-xs text-amber-600 hover:text-amber-500 text-center"
                  >
                    View all saved searches
                  </Link>
                </div>
              )}

              {/* Filters Header */}
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-semibold text-foreground flex items-center gap-2">
                  <SlidersHorizontal className="w-4 h-4" />
                  Filters
                </h2>
              </div>

              {/* Price Range - Hidden for Jobs category */}
              {getPricePresets(filters.category) !== null && (
              <div className="bg-card rounded-xl border border-border p-3 mb-4">
                <h3 className="text-xs font-medium text-muted-foreground mb-2">Price Range</h3>
                <div className="space-y-2">
                  <div className="flex gap-1.5 items-center">
                    <input
                      type="number"
                      placeholder="Min"
                      value={filters.min_price}
                      onChange={(e) => updateFilter('min_price', e.target.value)}
                      className="w-full min-w-0 px-3 py-2 text-sm border border-border rounded-xl bg-card text-foreground focus:border-primary focus:ring-1 focus:ring-primary/20 outline-none transition-colors"
                    />
                    <span className="text-muted-foreground/50 text-xs flex-shrink-0">–</span>
                    <input
                      type="number"
                      placeholder="Max"
                      value={filters.max_price}
                      onChange={(e) => updateFilter('max_price', e.target.value)}
                      className="w-full min-w-0 px-3 py-2 text-sm border border-border rounded-xl bg-card text-foreground focus:border-primary focus:ring-1 focus:ring-primary/20 outline-none transition-colors"
                    />
                  </div>
                  {/* 3 Quick price pills */}
                  <div className="flex gap-1">
                    {(getPricePresets(filters.category) || PRICE_PRESETS.default).map((preset, i) => (
                      <button
                        key={i}
                        onClick={() => updatePriceFilter(preset.min, preset.max)}
                        className={`flex-1 px-2 py-1.5 text-xs rounded-lg transition-colors ${
                          filters.min_price === preset.min && filters.max_price === preset.max
                            ? 'bg-primary text-white' 
                            : 'bg-secondary hover:bg-secondary/80 text-foreground'
                        }`}
                      >
                        {preset.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              )}

              {/* Listing Type - Compact Dropdown */}
              <div className="bg-card rounded-xl border border-border p-3 mb-4">
                <h3 className="text-xs font-medium text-muted-foreground mb-2">
                  {language === 'nl' ? 'Type Advertentie' : language === 'es' ? 'Tipo de Anuncio' : 'Listing Type'}
                </h3>
                <SafariDropdown
                  value={filters.listing_type}
                  onChange={(value) => updateFilter('listing_type', value)}
                  searchable={false}
                  options={[
                    { value: '', label: language === 'nl' ? 'Alle Types' : language === 'es' ? 'Todos los Tipos' : 'All Types' },
                    { value: 'sale', label: getListingTypeLabel('sale', language) },
                    { value: 'rent', label: getListingTypeLabel('rent', language) },
                    { value: 'trade', label: getListingTypeLabel('trade', language) },
                    { value: 'service', label: getListingTypeLabel('service', language) },
                    { value: 'yard_sale', label: getListingTypeLabel('yard_sale', language) },
                    { value: 'auction', label: getListingTypeLabel('auction', language) }
                  ]}
                  placeholder={language === 'nl' ? 'Alle Types' : language === 'es' ? 'Todos los Tipos' : 'All Types'}
                  testId="sidebar-listing-type-dropdown"
                />
              </div>

              {/* Dynamic Category Filters OR Subcategory Picker */}
              {filters.category && filters.subcategory ? (
                /* Category + Subcategory selected - show dynamic filters */
                <div className="bg-card rounded-xl border border-border p-3">
                  <h3 className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1.5">
                    <Filter className="w-3.5 h-3.5" />
                    {activeCategoryInfo?.name || 'Category'} Filters
                  </h3>
                  <DesktopDynamicFilters
                    categoryId={filters.category}
                    subcategory={filters.subcategory}
                    activeFilters={filters}
                    onFilterChange={updateFilter}
                    onFiltersReset={resetDynamicFilters}
                  />
                </div>
              ) : filters.category && activeCategoryInfo?.subcategories?.length > 0 ? (
                /* Category selected but no subcategory - show subcategory picker */
                <div className="bg-card rounded-xl border border-border p-3">
                  <h3 className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1.5">
                    <ChevronRight className="w-3.5 h-3.5" />
                    {activeCategoryInfo?.name || 'Category'}
                  </h3>
                  <div className="space-y-0.5">
                    {activeCategoryInfo.subcategories.map((sub) => {
                      const subId = typeof sub === 'object' ? sub.id : sub;
                      const subName = typeof sub === 'object' ? sub.name : sub;
                      return (
                        <button
                          key={subId}
                          onClick={() => updateFilter('subcategory', subId)}
                          className={`w-full text-left px-2.5 py-1.5 rounded-lg text-sm transition-colors ${filters.subcategory === subId ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-secondary text-foreground'}`}
                        >
                          {getSubcategoryTranslation(subName, language)}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : filters.category ? (
                /* Category selected but has no subcategories - show dynamic filters directly */
                <div className="mt-4 bg-card rounded-xl border border-border p-3">
                  <h3 className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1.5">
                    <Filter className="w-3.5 h-3.5" />
                    {activeCategoryInfo?.name || 'Category'} Filters
                  </h3>
                  <DesktopDynamicFilters
                    categoryId={filters.category}
                    subcategory={filters.subcategory}
                    activeFilters={filters}
                    onFilterChange={updateFilter}
                    onFiltersReset={resetDynamicFilters}
                  />
                </div>
              ) : (
                /* No category selected - show category picker */
                <div className="bg-card rounded-xl border border-border p-3">
                  <h3 className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1.5">
                    <Grid3x3 className="w-3.5 h-3.5" />
                    {language === 'nl' ? 'Categorieën' : language === 'es' ? 'Categorías' : 'Categories'}
                  </h3>
                  <div className="space-y-0.5 max-h-[300px] overflow-y-auto scrollbar-thin">
                    {categories.map((cat) => (
                      <button
                        key={cat.id}
                        onClick={() => updateFilter('category', cat.id)}
                        className="w-full text-left px-2.5 py-1.5 rounded-lg text-sm transition-colors hover:bg-secondary text-foreground"
                        data-testid={`sidebar-category-${cat.id}`}
                      >
                        {getCategoryTranslation(cat.name, language)}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </aside>

          {/* Main Content Area */}
          <div className="flex-1 min-w-0">
            {/* Top Bar: Results count, Sort, Mobile Filter Toggle - Hidden on mobile (shown in sticky header) */}
            <div className="hidden md:flex items-center justify-between mb-4 gap-4">
              <div className="flex items-center gap-3 flex-wrap">
                <p className="text-sm text-muted-foreground">
                  <span className="font-medium text-foreground">{total}</span> results
                </p>
                
                {/* Interactive Island Indicator Chip with Dropdown */}
                <div className="relative">
                  <button
                    onClick={() => setShowIslandPicker(!showIslandPicker)}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-all cursor-pointer ${
                      currentIsland === 'curacao' ? 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20 hover:bg-blue-500/20' :
                      currentIsland === 'aruba' ? 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20 hover:bg-red-500/20' :
                      currentIsland === 'bonaire' ? 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400 border-yellow-500/20 hover:bg-yellow-500/20' :
                      'bg-secondary text-muted-foreground border-border hover:bg-secondary/80'
                    }`}
                    data-testid="island-indicator-chip"
                  >
                    <MapPin className="w-3 h-3" />
                    <span className="opacity-70 text-[10px] uppercase tracking-wider font-bold">Island:</span>
                    <span className="capitalize">
                      {currentIsland === 'curacao' ? '🇨🇼 Curaçao' :
                       currentIsland === 'aruba' ? '🇦🇼 Aruba' :
                       '🇧🇶 Bonaire'}
                    </span>
                    <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${showIslandPicker ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {/* Island Picker Dropdown */}
                  {showIslandPicker && (
                    <>
                      <div 
                        className="fixed inset-0 z-40" 
                        onClick={() => setShowIslandPicker(false)}
                      />
                      <div className="absolute top-full left-0 mt-2 w-52 bg-card border border-border rounded-xl shadow-xl z-[60] overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                        <div className="p-2">
                          <p className="text-[10px] text-muted-foreground uppercase tracking-wider px-2 py-1.5 mb-1 font-semibold">
                            Select Island
                          </p>
                          {[
                            { id: 'curacao', name: 'Curaçao', flag: '🇨🇼', color: 'bg-blue-500/10' },
                            { id: 'aruba', name: 'Aruba', flag: '🇦🇼', color: 'bg-red-500/10' },
                            { id: 'bonaire', name: 'Bonaire', flag: '🇧🇶', color: 'bg-yellow-500/10' }
                          ].map((island) => (
                            <button
                              key={island.id}
                              onClick={() => {
                                setCurrentIsland(island.id);
                                setShowIslandPicker(false);
                              }}
                              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                                currentIsland === island.id
                                  ? `${island.color} ring-1 ring-inset ring-primary/20`
                                  : 'hover:bg-secondary/70 text-foreground'
                              }`}
                              data-testid={`island-option-${island.id}`}
                            >
                              <span className="text-lg">{island.flag}</span>
                              <span className="flex-1 text-left text-sm font-medium">{island.name}</span>
                              {currentIsland === island.id && (
                                <Check className="w-4 h-4 text-primary" />
                              )}
                            </button>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>
                
                {/* Mobile Filter Button */}
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="lg:hidden flex items-center gap-2 px-3 py-1.5 bg-secondary rounded-lg text-sm"
                >
                  <SlidersHorizontal className="w-4 h-4" />
                  Filters
                  {activeFiltersCount > 0 && (
                    <span className="w-5 h-5 bg-primary text-white text-xs rounded-full flex items-center justify-center">
                      {activeFiltersCount}
                    </span>
                  )}
                </button>
              </div>
              
              <div className="flex items-center gap-2">
                {/* Save Search */}
                {hasActiveSearch && (
                  <button
                    onClick={handleSaveSearch}
                    className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 text-sm text-amber-600 bg-amber-500/10 rounded-lg hover:bg-amber-500/20 transition-colors"
                  >
                    <BellPlus className="w-4 h-4" />
                    Save
                  </button>
                )}
                
                {/* View Mode Switcher */}
                <div className="flex items-center bg-secondary/70 rounded-lg p-0.5" data-testid="view-mode-switcher">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-1.5 rounded-md transition-all ${
                      viewMode === 'grid' 
                        ? 'bg-primary text-primary-foreground shadow-sm' 
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                    title={t('grid_view') || 'Grid view'}
                    data-testid="view-mode-grid"
                  >
                    <Grid3x3 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-1.5 rounded-md transition-all ${
                      viewMode === 'list' 
                        ? 'bg-primary text-primary-foreground shadow-sm' 
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                    title={t('list_view') || 'List view'}
                    data-testid="view-mode-list"
                  >
                    <List className="w-4 h-4" />
                  </button>
                </div>
                
                {/* Sort Dropdown - Compact style to match Save button */}
                <div ref={sortDropdownRef} className="relative" data-testid="sort-dropdown-container">
                  <button
                    onClick={() => setShowSortDropdown(!showSortDropdown)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-secondary hover:bg-secondary/80 rounded-lg transition-colors"
                    data-testid="sort-dropdown-trigger"
                  >
                    <span>{filters.sort === 'price_low' ? 'Price ↑' : filters.sort === 'price_high' ? 'Price ↓' : filters.sort === 'popular' ? 'Popular' : 'Newest'}</span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${showSortDropdown ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {showSortDropdown && (
                    <div 
                      className="absolute right-0 top-full mt-1 w-48 bg-card border border-border rounded-lg shadow-lg overflow-hidden z-30"
                      data-testid="sort-dropdown-menu"
                    >
                      {[
                        { value: 'newest', label: 'Newest' },
                        { value: 'price_low', label: 'Price: Low to High' },
                        { value: 'price_high', label: 'Price: High to Low' },
                        { value: 'popular', label: 'Most Popular' }
                      ].map((option) => (
                        <button
                          key={option.value}
                          onClick={() => { updateFilter('sort', option.value); setShowSortDropdown(false); }}
                          className={`w-full px-3 py-2 text-sm text-left flex items-center justify-between hover:bg-secondary transition-colors ${
                            filters.sort === option.value ? 'bg-primary/10 text-primary font-medium' : 'text-foreground'
                          }`}
                        >
                          <span>{option.label}</span>
                          {filters.sort === option.value && <Check className="w-4 h-4" />}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Applied Filters Chips */}
            <AppliedFiltersChips 
              filters={filters} 
              categories={categories}
              onRemove={updateFilter}
              onClearAll={clearFiltersKeepCategory}
              onClearPrice={clearPriceFilter}
              onRemoveCategory={handleRemoveCategoryFilter}
              language={language}
              onSaveSearch={handleSaveSearch}
              hasActiveSearch={hasActiveSearch}
              user={user}
            />

            {/* Mobile More Filters Bottom Sheet */}
            <BottomSheet
              isOpen={showFilters}
              onClose={() => setShowFilters(false)}
              title="Filters"
              testId="more-filters-sheet"
              fullHeight={true}
              footer={
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowFilters(false)}
                    className="flex-1 py-3 text-sm font-medium text-muted-foreground border border-border rounded-xl hover:bg-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => setShowFilters(false)}
                    className="flex-1 py-3 text-sm font-medium bg-primary text-primary-foreground rounded-xl hover:bg-primary/90"
                  >
                    Apply
                  </button>
                </div>
              }
            >
              <div className="p-4 space-y-5">
                
                {/* Listing Type - Dropdown */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    {language === 'nl' ? 'Type Advertentie' : language === 'es' ? 'Tipo de Anuncio' : 'Listing Type'}
                  </label>
                  <SafariDropdown
                    value={filters.listing_type}
                    onChange={(value) => updateFilter('listing_type', value)}
                    options={[
                      { value: '', label: language === 'nl' ? 'Alle Types' : language === 'es' ? 'Todos los Tipos' : 'All Types' },
                      { value: 'sale', label: getListingTypeLabel('sale', language) },
                      { value: 'rent', label: getListingTypeLabel('rent', language) },
                      { value: 'trade', label: getListingTypeLabel('trade', language) },
                      { value: 'service', label: getListingTypeLabel('service', language) },
                      { value: 'yard_sale', label: getListingTypeLabel('yard_sale', language) },
                      { value: 'auction', label: getListingTypeLabel('auction', language) }
                    ]}
                    placeholder={language === 'nl' ? 'Selecteer Type' : language === 'es' ? 'Seleccionar Tipo' : 'Select Type'}
                    testId="mobile-listing-type-dropdown"
                  />
                </div>
                
                {/* Price Range - Quick access */}
                {getPricePresets(filters.category) !== null && (
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Price Range</label>
                    <div className="flex items-center gap-2">
                      <div className="relative flex-1">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                        <input
                          type="number"
                          placeholder="Min"
                          value={filters.min_price}
                          onChange={(e) => updateFilter('min_price', e.target.value)}
                          className="w-full pl-7 pr-3 py-2.5 text-sm border border-border rounded-xl bg-secondary/30 text-foreground focus:bg-background focus:border-primary outline-none"
                        />
                      </div>
                      <span className="text-muted-foreground">–</span>
                      <div className="relative flex-1">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                        <input
                          type="number"
                          placeholder="Max"
                          value={filters.max_price}
                          onChange={(e) => updateFilter('max_price', e.target.value)}
                          className="w-full pl-7 pr-3 py-2.5 text-sm border border-border rounded-xl bg-secondary/30 text-foreground focus:bg-background focus:border-primary outline-none"
                        />
                      </div>
                    </div>
                    {/* Quick Price Presets */}
                    {getPricePresets(filters.category) && (
                      <div className="flex gap-2 mt-2">
                        {getPricePresets(filters.category).map((preset, idx) => (
                          <button
                            key={idx}
                            onClick={() => {
                              updateFilter('min_price', preset.min);
                              updateFilter('max_price', preset.max);
                            }}
                            className={`flex-1 py-1.5 text-xs font-medium rounded-lg transition-all ${
                              filters.min_price === preset.min && filters.max_price === preset.max
                                ? 'bg-primary text-primary-foreground'
                                : 'bg-secondary/50 text-foreground hover:bg-secondary'
                            }`}
                          >
                            {preset.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                
                {/* Category-Specific Filters - Flat layout for mobile */}
                {filters.category && (
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      {activeCategoryInfo?.name || 'Category'} Options
                    </label>
                    <DynamicFilters
                      categoryId={filters.category}
                      subcategory={filters.subcategory}
                      activeFilters={filters}
                      onFilterChange={updateFilter}
                      onFiltersReset={resetDynamicFilters}
                      flat={true}
                      showTitle={false}
                    />
                  </div>
                )}
              </div>
            </BottomSheet>

            {/* Personalized Recommendations - Shows based on recent searches */}
            <PersonalizedRecommendations
              recentSearches={recentSearches}
              currentSearchQuery={filters.search}
              currentCategory={filters.category}
              onClearHistory={clearRecentSearches}
            />

            {/* Listings Grid */}
            {loading ? (
              <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
                {[...Array(12)].map((_, i) => (
                  <div key={i} className="bg-card rounded-xl overflow-hidden border border-border">
                    <div className="aspect-[4/3] skeleton"></div>
                    <div className="p-3 space-y-2">
                      <div className="h-3 skeleton rounded w-3/4"></div>
                      <div className="h-4 skeleton rounded w-1/2"></div>
                      <div className="h-3 skeleton rounded w-full"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : listings.length > 0 ? (
              <>
                <div 
                  className={viewMode === 'list' 
                    ? 'flex flex-col gap-3' 
                    : 'grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4'
                  } 
                  data-testid="listings-grid"
                >
                  {(() => {
                    // Build list of items with ads inserted at their configured intervals
                    const items = [];
                    
                    // Shuffle ads array for rotation (different ad each interval)
                    const shuffledAds = [...listingGridAds].sort(() => Math.random() - 0.5);
                    let adIndex = 0;
                    
                    // Find the minimum interval from all ads (or use global default of 8)
                    const globalInterval = shuffledAds.length > 0 
                      ? Math.min(...shuffledAds.map(ad => ad.listing_interval || 8))
                      : 8;
                    
                    listings.forEach((listing, index) => {
                      // First add the listing
                      items.push({
                        type: 'listing',
                        listing: listing,
                        key: listing.id
                      });
                      
                      // After adding the listing, check if an ad should appear
                      // Position is 1-indexed (after listing 1, after listing 2, etc.)
                      const position = index + 1;
                      
                      // Show an ad after every 'globalInterval' listings, rotating through available ads
                      if (shuffledAds.length > 0 && position % globalInterval === 0) {
                        const ad = shuffledAds[adIndex % shuffledAds.length];
                        items.push({
                          type: 'ad',
                          ad: ad,
                          key: `ad-${ad.id}-after-${position}`
                        });
                        adIndex++;
                      }
                    });
                    
                    return items.map((item) => {
                      if (item.type === 'ad') {
                        const adIsVideo = isVideoUrl(item.ad.image_url);
                        return (
                          <a
                            key={item.key}
                            href={item.ad.link_url || '#'}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="relative group h-full min-h-[280px] rounded-2xl overflow-hidden hover:shadow-lg transition-all duration-300 border border-border"
                            data-testid={`listing-grid-ad-${item.ad.id}`}
                            onClick={() => {
                              // Track click with placement context
                              fetch(`${API}/banners/${item.ad.id}/click?placement_id=listing_grid`, { method: 'POST' }).catch(() => {});
                            }}
                          >
                            {/* Full-card background - video or image */}
                            {adIsVideo ? (
                              <video
                                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 pointer-events-none"
                                autoPlay
                                loop
                                muted
                                playsInline
                                webkit-playsinline="true"
                                x-webkit-airplay="deny"
                                disablePictureInPicture
                                preload="auto"
                                poster={item.ad.poster_url}
                              >
                                <source src={item.ad.image_url} type="video/mp4" />
                              </video>
                            ) : (
                              <img 
                                src={item.ad.image_url || '/placeholder-ad.jpg'} 
                                alt={item.ad.title || 'Ad'} 
                                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                              />
                            )}
                            
                            {/* Gradient overlay for text readability */}
                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                            
                            {/* AD badge - top left */}
                            <span className="absolute top-2 left-2 px-1.5 py-0.5 bg-black/60 backdrop-blur-sm text-white text-[10px] font-medium rounded z-10">
                              Ad
                            </span>
                            
                            {/* Text overlay - bottom */}
                            <div className="absolute bottom-0 left-0 right-0 p-2 sm:p-3 z-10">
                              {item.ad.display_text && (
                                <h3 className="font-semibold text-white text-xs sm:text-sm line-clamp-2 drop-shadow-md">
                                  {item.ad.display_text}
                                </h3>
                              )}
                              {item.ad.button_text && (
                                <span className="mt-1 inline-block text-[10px] sm:text-xs text-white/90 font-medium">
                                  {item.ad.button_text} →
                                </span>
                              )}
                            </div>
                          </a>
                        );
                      }
                      
                      return (
                        <ListingCard 
                          key={item.key} 
                          listing={item.listing}
                          variant={viewMode}
                        />
                      );
                    });
                  })()}
                </div>

                {/* Load More Button */}
                {listings.length < total && (
                  <div className="flex flex-col items-center gap-3 mt-8">
                    <button
                      onClick={handleLoadMore}
                      disabled={loadingMore}
                      className="px-6 py-3 bg-primary text-primary-foreground rounded-xl font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center gap-2"
                      data-testid="load-more-button"
                    >
                      {loadingMore ? (
                        <>
                          <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          {language === 'nl' ? 'Laden...' : language === 'es' ? 'Cargando...' : 'Loading...'}
                        </>
                      ) : (
                        <>
                          {language === 'nl' 
                            ? `Laad meer (${listings.length} van ${total})` 
                            : language === 'es' 
                              ? `Cargar más (${listings.length} de ${total})`
                              : `Load more (${listings.length} of ${total})`}
                        </>
                      )}
                    </button>
                    <p className="text-xs text-muted-foreground">
                      {language === 'nl' 
                        ? `Pagina ${currentPage} van ${pages}` 
                        : language === 'es' 
                          ? `Página ${currentPage} de ${pages}`
                          : `Page ${currentPage} of ${pages}`}
                    </p>
                  </div>
                )}
              </>
            ) : fallbackResults.length > 0 ? (
              /* ============== FALLBACK SEARCH RESULTS ============== */
              <div className="py-8" data-testid="fallback-search-results">
                {/* Friendly empty state message */}
                <div className="text-center mb-8 p-6 bg-secondary/30 rounded-2xl border border-border">
                  <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-amber-500/10 flex items-center justify-center">
                    <Search className="w-7 h-7 text-amber-500" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    We couldn't find "{fallbackSearchTerm}" in {categories.find(c => c.id === fallbackSearchedCategory)?.name || formatCategorySlug(fallbackSearchedCategory)}
                  </h3>
                  <p className="text-muted-foreground text-sm mb-4">
                    But we found some matches in other categories that might interest you
                  </p>
                  
                  {/* Search everywhere button */}
                  <button
                    onClick={clearCategoryKeepSearch}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-white rounded-xl text-sm font-medium hover:bg-primary/90 transition-colors"
                    data-testid="search-everywhere-btn"
                  >
                    <Search className="w-4 h-4" />
                    Search everywhere instead
                  </button>
                </div>
                
                {/* Suggested categories chips */}
                {suggestedCategories.length > 0 && (
                  <div className="mb-6">
                    <p className="text-sm text-muted-foreground mb-3">Quick switch to category:</p>
                    <div className="flex flex-wrap gap-2">
                      {suggestedCategories.map((cat) => {
                        const categoryData = categories.find(c => c.id === cat);
                        const SuggestedCatIcon = categoryData ? getIconComponent(categoryData.icon) : Package;
                        return (
                          <button
                            key={cat}
                            onClick={() => updateFilter('category', cat)}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-card border border-border rounded-xl text-sm font-medium hover:border-primary hover:bg-primary/5 transition-all"
                            data-testid={`suggested-category-${cat}`}
                          >
                            <SuggestedCatIcon className="w-4 h-4" />
                            <span>{categoryData?.name || cat}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                {/* Alternative results section */}
                <div className="mb-4">
                  <h4 className="text-base font-semibold text-foreground flex items-center gap-2">
                    <Star className="w-5 h-5 text-amber-500" />
                    Results from other categories
                  </h4>
                </div>
                
                {/* Fallback listings grid - same styling as main listings grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {fallbackResults.map((listing) => (
                    <ListingCard
                      key={listing.id}
                      listing={listing}
                    />
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-16 min-h-[400px]">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-secondary flex items-center justify-center">
                  <Search className="w-8 h-8 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  {language === 'nl' ? 'Geen advertenties gevonden' : language === 'es' ? 'No se encontraron anuncios' : 'No listings found'}
                </h3>
                <p className="text-muted-foreground mb-4 text-sm">
                  {language === 'nl' ? 'Probeer je filters aan te passen' : language === 'es' ? 'Intenta ajustar tus filtros' : 'Try adjusting your filters'}
                </p>
                <button
                  onClick={clearFiltersKeepCategory}
                  className="px-4 py-2 bg-primary text-white rounded-lg text-sm hover:bg-primary/90"
                >
                  {language === 'nl' ? 'Filters wissen' : language === 'es' ? 'Borrar filtros' : 'Clear Filters'}
                </button>
              </div>
            )}
          </div>
        </div>
        </>
        )}
      </div>

      <Footer />
    </div>
  );
};

/**
 * AppliedFiltersChips - Shows active filters as removable chips
 */
const AppliedFiltersChips = ({ filters, categories, onRemove, onClearAll, onClearPrice, onRemoveCategory, language = 'en', onSaveSearch, hasActiveSearch, user }) => {
  // Helper to get translated subcategory name
  const getSubcategoryName = (subcategoryId, categoryId) => {
    if (!subcategoryId || !categoryId) return subcategoryId;
    
    const category = categories.find(c => c.id === categoryId);
    if (category && category.subcategories) {
      const subcategory = category.subcategories.find(s => {
        const subId = typeof s === 'object' ? s.id : s;
        return subId === subcategoryId;
      });
      
      if (subcategory) {
        // Handle both object format {name: {en, nl, es}} and string format
        if (typeof subcategory === 'object' && subcategory.name) {
          if (typeof subcategory.name === 'object') {
            return subcategory.name[language] || subcategory.name.en || subcategoryId;
          }
          return subcategory.name;
        }
      }
    }
    
    // Fallback to i18n translation helper for legacy/unmatched subcategories
    // This handles both slug-style IDs and proper names
    return getSubcategoryTranslation(subcategoryId, language);
  };

  // Build list of active filters to display
  const activeFilters = [];
  
  // Subcategory filter - don't show "all-*" as a chip (it's just "View All" mode indicator)
  const hasSpecificSubcategory = filters.subcategory && !filters.subcategory.startsWith('all-');
  
  // Category filter - uses special handler that navigates to /categories
  // HIDE category pill if a specific subcategory is selected (to reduce redundancy)
  if (filters.category && !hasSpecificSubcategory) {
    const cat = categories.find(c => c.id === filters.category);
    activeFilters.push({
      key: 'category',
      label: 'Category',
      value: cat?.name || formatCategorySlug(filters.category),
      color: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20',
      onRemove: onRemoveCategory // Special handler to navigate to /categories
    });
  }
  
  // Subcategory filter - with translation
  if (hasSpecificSubcategory) {
    const translatedSubcategoryName = getSubcategoryName(filters.subcategory, filters.category);
    activeFilters.push({
      key: 'subcategory',
      label: language === 'nl' ? 'Subcategorie' : language === 'es' ? 'Subcategoría' : 'Subcategory',
      value: translatedSubcategoryName,
      color: 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border-indigo-500/20'
    });
  }
  
  // Type filter
  if (filters.listing_type) {
    const typeLabels = {
      'sale': 'For Sale',
      'rent': 'For Rent',
      'auction': 'Auction',
      'trade': 'Trade'
    };
    activeFilters.push({
      key: 'listing_type',
      label: 'Type',
      value: typeLabels[filters.listing_type] || filters.listing_type,
      color: 'bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20'
    });
  }
  
  // Price range
  if (filters.min_price || filters.max_price) {
    const priceText = filters.min_price && filters.max_price 
      ? `$${filters.min_price} - $${filters.max_price}`
      : filters.min_price 
        ? `From $${filters.min_price}`
        : `Up to $${filters.max_price}`;
    activeFilters.push({
      key: 'price',
      label: 'Price',
      value: priceText,
      color: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20',
      onRemove: onClearPrice
    });
  }
  
  // Search term
  if (filters.search) {
    activeFilters.push({
      key: 'search',
      label: 'Search',
      value: `"${filters.search}"`,
      color: 'bg-primary/10 text-primary border-primary/20'
    });
  }
  
  // Dynamic property filters (props_*)
  Object.entries(filters).forEach(([key, value]) => {
    if (!key.startsWith('props_') || !value) return;
    
    const propKey = key.replace('props_', '').replace('_min', ' (min)').replace('_max', ' (max)');
    const displayKey = propKey
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    
    activeFilters.push({
      key: key,
      label: displayKey,
      value: value === 'true' ? 'Yes' : value === 'false' ? 'No' : String(value),
      color: 'bg-teal-500/10 text-teal-600 dark:text-teal-400 border-teal-500/20'
    });
  });
  
  if (activeFilters.length === 0) return null;
  
  return (
    <div className="mt-3 mb-6 flex flex-wrap items-center gap-2" data-testid="applied-filters">
      <span className="text-xs text-muted-foreground font-medium">Filters:</span>
      {activeFilters.map((filter) => (
        <span
          key={filter.key}
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${filter.color} transition-all hover:opacity-80`}
          data-testid={`filter-chip-${filter.key}`}
        >
          <span className="opacity-70">{filter.label}:</span>
          <span>{filter.value}</span>
          <button
            onClick={() => filter.onRemove ? filter.onRemove() : onRemove(filter.key, '')}
            className="ml-0.5 hover:opacity-100 opacity-60 transition-opacity"
            aria-label={`Remove ${filter.label} filter`}
          >
            <X className="w-3 h-3" />
          </button>
        </span>
      ))}
      {activeFilters.length > 1 && (
        <button
          onClick={onClearAll}
          className="text-xs text-muted-foreground hover:text-foreground transition-colors underline underline-offset-2"
          data-testid="clear-all-filters-chip"
        >
          Clear all
        </button>
      )}
      {/* Save Search Button - Mobile only, shows when user is logged in or to prompt login */}
      {hasActiveSearch && (
        <button
          onClick={onSaveSearch}
          className="md:hidden inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20 transition-all hover:bg-amber-500/20"
          data-testid="mobile-save-search-button"
        >
          <Bookmark className="w-3 h-3" />
          <span>{language === 'nl' ? 'Bewaar' : language === 'es' ? 'Guardar' : 'Save'}</span>
        </button>
      )}
    </div>
  );
};

/**
 * DesktopDynamicFilters - Renders category-specific filters directly (no collapsible groups)
 * Uses SafariDropdown for consistent look-and-feel with CreateListingPage
 */
const DesktopDynamicFilters = ({ 
  categoryId, 
  subcategory,
  activeFilters = {},
  onFilterChange,
  onFiltersReset
}) => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch filterable properties when category changes
  useEffect(() => {
    if (!categoryId) {
      setProperties([]);
      return;
    }

    const fetchProperties = async () => {
      setLoading(true);
      try {
        let url = `${API}/categories/${categoryId}/properties?lang=en`;
        if (subcategory && !subcategory.startsWith('all-')) {
          url += `&subcategory=${encodeURIComponent(subcategory)}`;
        }
        const response = await axios.get(url);
        // Only keep filterable properties, sorted by sort_order
        const filterable = (response.data.properties || [])
          .filter(p => p.filterable)
          .sort((a, b) => (a.sort_order || 999) - (b.sort_order || 999));
        setProperties(filterable);
      } catch (error) {
        console.error('Failed to fetch properties:', error);
        setProperties([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProperties();
  }, [categoryId, subcategory]);

  // Count active dynamic filters
  const activeCount = Object.keys(activeFilters).filter(k => k.startsWith('props_') && activeFilters[k]).length;

  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map(i => (
          <div key={i} className="space-y-2">
            <div className="h-4 w-20 bg-secondary/50 rounded animate-pulse" />
            <div className="h-10 bg-secondary/30 rounded-xl animate-pulse" />
          </div>
        ))}
      </div>
    );
  }

  if (properties.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">No specific filters available for this category.</p>
    );
  }

  return (
    <div className="space-y-4">
      {/* Reset button when filters are active */}
      {activeCount > 0 && (
        <div className="flex justify-end">
          <button
            onClick={onFiltersReset}
            className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1 transition-colors"
            data-testid="reset-desktop-dynamic-filters"
          >
            <X className="w-3 h-3" />
            Reset filters ({activeCount})
          </button>
        </div>
      )}
      
      {/* Render each property as a filter field */}
      {properties.map(prop => (
        <DesktopFilterField
          key={prop.key}
          property={prop}
          value={activeFilters[`props_${prop.key}`]}
          minValue={activeFilters[`props_${prop.key}_min`]}
          maxValue={activeFilters[`props_${prop.key}_max`]}
          onChange={onFilterChange}
        />
      ))}
    </div>
  );
};

/**
 * DesktopFilterField - Renders the appropriate input for a property type
 * Uses SafariDropdown for predefined_list types
 */
const DesktopFilterField = ({ property, value, minValue, maxValue, onChange }) => {
  const { key, name, type, options, unit, min_value, max_value } = property;
  const label = typeof name === 'object' ? name.en : name;
  const propKey = `props_${key}`;

  // Boolean filter (checkbox style)
  if (type === 'boolean') {
    const isChecked = value === 'true' || value === true;
    return (
      <label 
        className="flex items-center gap-3 cursor-pointer group py-1" 
        data-testid={`desktop-filter-${key}`}
      >
        <div className="relative">
          <input
            type="checkbox"
            checked={isChecked}
            onChange={(e) => onChange(propKey, e.target.checked ? 'true' : '')}
            className="sr-only peer"
          />
          <div className={`w-5 h-5 rounded border-2 transition-all duration-200 flex items-center justify-center
            ${isChecked 
              ? 'bg-primary border-primary' 
              : 'border-border bg-card group-hover:border-primary/50'
            }`}
          >
            {isChecked && (
              <svg className="w-3 h-3 text-primary-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            )}
          </div>
        </div>
        <span className={`text-sm transition-colors ${isChecked ? 'text-primary font-medium' : 'text-foreground group-hover:text-primary'}`}>
          {label}
        </span>
      </label>
    );
  }

  // Predefined list (dropdown using SafariDropdown)
  if (type === 'predefined_list' && options?.length) {
    const dropdownOptions = [
      { value: '', label: `Any ${label}` },
      ...options.map(opt => {
        const optValue = typeof opt === 'object' ? (opt.value || String(opt)) : String(opt);
        const optLabel = typeof opt === 'object' ? (opt.label || opt.value || String(opt)) : String(opt);
        return { value: optValue, label: optLabel };
      })
    ];
    
    return (
      <div data-testid={`desktop-filter-${key}`}>
        <label className="block text-xs text-muted-foreground mb-1.5">{label}</label>
        <SafariDropdown
          value={value || ''}
          onChange={(val) => onChange(propKey, val)}
          options={dropdownOptions}
          placeholder={`Any ${label}`}
          testId={`desktop-filter-dropdown-${key}`}
        />
      </div>
    );
  }

  // Number filter (range inputs)
  if (type === 'number') {
    return (
      <div data-testid={`desktop-filter-${key}`}>
        <label className="block text-xs text-muted-foreground mb-1.5">
          {label} {unit && <span className="text-muted-foreground/60">({unit})</span>}
        </label>
        <div className="flex gap-1.5 items-center">
          <input
            type="number"
            placeholder="Min"
            value={minValue || ''}
            onChange={(e) => onChange(`props_${key}_min`, e.target.value)}
            className="w-full min-w-0 px-3 py-2.5 text-sm border border-border rounded-xl bg-card text-foreground focus:border-primary focus:ring-1 focus:ring-primary/20 outline-none transition-colors"
          />
          <span className="text-muted-foreground/50 text-xs flex-shrink-0">–</span>
          <input
            type="number"
            placeholder="Max"
            value={maxValue || ''}
            onChange={(e) => onChange(`props_${key}_max`, e.target.value)}
            className="w-full min-w-0 px-3 py-2.5 text-sm border border-border rounded-xl bg-card text-foreground focus:border-primary focus:ring-1 focus:ring-primary/20 outline-none transition-colors"
          />
        </div>
      </div>
    );
  }

  // Text filter (simple input)
  return (
    <div data-testid={`desktop-filter-${key}`}>
      <label className="block text-xs text-muted-foreground mb-1.5">{label}</label>
      <input
        type="text"
        placeholder={`Search ${label.toLowerCase()}...`}
        value={value || ''}
        onChange={(e) => onChange(propKey, e.target.value)}
        className="w-full px-4 py-2.5 text-sm border border-border rounded-xl bg-card text-foreground focus:border-primary focus:ring-1 focus:ring-primary/20 outline-none transition-colors"
      />
    </div>
  );
};

export default MarketplacePage;
